import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BanknotesIcon,
  DevicePhoneMobileIcon,
  CreditCardIcon,
  DocumentTextIcon,
  UserPlusIcon,
  GiftIcon,
  ChevronLeftIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import BillPay from './quickActions/BillPay';
import MobileDeposit from './quickActions/MobileDeposit';
import Statements from './quickActions/Statements';
import CardServices from './quickActions/CardServices';

const QUICK_ACTIONS = [
  {
    id: 'billpay',
    label: 'Bill pay',
    description: 'Pay utilities and bills',
    icon: BanknotesIcon,
    accent: 'primary',
  },
  {
    id: 'deposit',
    label: 'Check deposit',
    description: 'Deposit checks with your phone',
    icon: DevicePhoneMobileIcon,
    accent: 'emerald',
  },
  {
    id: 'cards',
    label: 'Card services',
    description: 'Freeze, limits and more',
    icon: CreditCardIcon,
    accent: 'slate',
  },
  {
    id: 'statements',
    label: 'Statements',
    description: 'View and download PDFs',
    icon: DocumentTextIcon,
    accent: 'primary',
  },
  {
    id: 'paycontact',
    label: 'Pay a contact',
    description: 'Send money to friends & family',
    icon: UserPlusIcon,
    accent: 'secondary',
  },
  {
    id: 'rewards',
    label: 'Cashback',
    description: 'Earn rewards on spending',
    icon: GiftIcon,
    accent: 'amber',
  },
];

const VALID_IDS = new Set(QUICK_ACTIONS.map((a) => a.id));

const accentStyles = {
  primary: 'bg-primary-50 text-primary-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  slate: 'bg-slate-100 text-slate-600',
  secondary: 'bg-secondary-50 text-secondary-600',
  amber: 'bg-amber-50 text-amber-600',
};

export default function QuickActions({ accounts }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [activeAction, setActiveAction] = useState(null);

  useEffect(() => {
    const hash = (location.hash || '').replace(/^#/, '');
    if (hash.startsWith('quick-actions-')) {
      const id = hash.replace('quick-actions-', '');
      if (VALID_IDS.has(id)) {
        setActiveAction(id);
        sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      }
    } else {
      setActiveAction(null);
    }
  }, [location.hash]);

  const handleSetActiveAction = (id) => {
    setActiveAction(id);
    navigate('/dashboard#quick-actions-' + id, { replace: true });
  };

  const handleBack = () => {
    setActiveAction(null);
    navigate('/dashboard', { replace: true });
  };

  const activeMeta = activeAction ? QUICK_ACTIONS.find((a) => a.id === activeAction) : null;

  return (
    <div ref={sectionRef} className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
      {!activeAction ? (
        <>
          <div className="border-b border-slate-100 dark:border-slate-700 px-4 sm:px-6 py-4">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">Banking services</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Pay bills, manage cards, view statements and more</p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                const style = accentStyles[action.accent] || accentStyles.primary;
                return (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() => handleSetActiveAction(action.id)}
                    data-action-id={action.id}
                    className="group flex items-center gap-4 rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-700/50 p-4 text-left shadow-sm transition-all hover:border-primary-200 dark:hover:border-primary-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
                  >
                    <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${style} dark:bg-slate-600 dark:text-slate-200`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                        {action.label}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{action.description}</p>
                    </div>
                    <ArrowRightIcon className="w-5 h-5 shrink-0 text-slate-300 dark:text-slate-500 group-hover:text-primary-500 group-hover:translate-x-0.5 transition-all" />
                  </button>
                );
              })}
            </div>
          </div>
        </>
      ) : (
        <div className="animate-fadeIn">
          <div className="border-b border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-700/30 px-4 sm:px-6 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 rounded-lg py-2 pr-2 pl-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-600 hover:text-slate-900 dark:hover:text-white hover:shadow-sm transition-colors"
              aria-label="Back to all services"
            >
              <ChevronLeftIcon className="w-5 h-5" />
              <span className="hidden sm:inline">All services</span>
            </button>
            {activeMeta && (() => {
              const Icon = activeMeta.icon;
              return (
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${accentStyles[activeMeta.accent] || accentStyles.primary} dark:bg-slate-600 dark:text-slate-200`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="font-semibold text-slate-900 dark:text-white truncate">{activeMeta.label}</span>
                </div>
              );
            })()}
          </div>

          <div className="p-4 sm:p-6">
            {activeAction === 'billpay' && <BillPay accounts={accounts} />}
            {activeAction === 'deposit' && (
              <MobileDeposit accounts={accounts} currentUser={currentUser} />
            )}
            {activeAction === 'statements' && (
              <Statements accounts={accounts} userEmail={currentUser?.email} />
            )}
            {activeAction === 'cards' && <CardServices />}
            {activeAction === 'paycontact' && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-700/30 p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-50 dark:bg-primary-900/50 flex items-center justify-center">
                  <UserPlusIcon className="w-7 h-7 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Pay a contact</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                  Send money to friends and family by mobile number. This feature is coming soon—we’ll notify you when it’s available.
                </p>
              </div>
            )}
            {activeAction === 'rewards' && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/50 dark:bg-slate-700/30 p-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
                  <GiftIcon className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">Cashback & rewards</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 max-w-sm mx-auto">
                  Our cashback programme is being updated. You’ll earn rewards on everyday spending—check back soon for details.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
